import React, {ReactElement, Suspense} from 'react';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ErrorBoundary} from 'react-error-boundary';
import {InvalidOrAbnormal} from '@di-shared/api/di/type';
import {WebviewUtils} from '@shared/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {CreatedMaintenanceRecordRequest} from '@di-shared/api/di/__generated';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';

interface WarningLightActions {
  linkToMaintenanceAppointment(): void;

  openSestManagementBottomSheet(): void;
}

interface SestDetailEngineOilConditionalRendererProps {
  vin: string;
  loadingFallback?: ReactElement;
  errorFallback?: ReactElement;

  ifNormalRender(actions: WarningLightActions): ReactElement;

  ifWarningLightRender(actions: WarningLightActions): ReactElement;
}

function SestDetailEngineOilConditionalRenderer({
  loadingFallback = <></>,
  errorFallback = <></>,
  ...props
}: SestDetailEngineOilConditionalRendererProps) {
  return (
    <ErrorBoundary fallback={errorFallback}>
      <Suspense fallback={loadingFallback}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({
  vin,
  ifNormalRender,
  ifWarningLightRender,
}: Omit<SestDetailEngineOilConditionalRendererProps, 'loadingFallback' | 'errorFallback'>) {
  const queryClient = useQueryClient();

  const {data} = useSuspenseQuery({
    queryKey: [ccWebviewUtils.getCarWarningLightStatus],
    queryFn: async () => {
      try {
        return await ccWebviewUtils.getCarWarningLightStatus();
      } catch (error) {
        return null;
      }
    },
    retry: 1,
  });

  function linkToMaintenanceAppointment() {
    WebviewUtils.openNewWebView({
      url: `${import.meta.env.VITE_CCI_DOMAIN}/maintenance/register/appointment?vin=${vin}`,
      isModal: true,
      isNavigationBar: true,
      isToolBar: false,
    });
  }

  async function openSestManagementBottomSheet() {
    const isChange = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(
      ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING.getFullPath(CreatedMaintenanceRecordRequest.sestCode._01),
      {
        vin,
      }
    );
    if (isChange) {
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.maintenancesSestController.getSest)]});
    }
  }

  if (data?.engineOil === InvalidOrAbnormal.Abnormal) {
    return ifWarningLightRender({linkToMaintenanceAppointment, openSestManagementBottomSheet});
  }
  return ifNormalRender({linkToMaintenanceAppointment, openSestManagementBottomSheet});
}

export default SestDetailEngineOilConditionalRenderer;
